import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { get, isEmpty, snakeCase } from 'lodash';

import CardDialog from '~/components/CardDialog';
import Grid from '~/components/core/Atomic/Grid/Grid';
import MenuItem from '~/components/core/Atomic/MenuItem';
import InnerCard from '~/components/core/Cards/InnerCard';
import CollapsibleWrapper from '~/components/core/Collapsible/CollapsibleWrapper';
import SwitchFormik from '~/components/core/Formik/SwitchFormik';
import {
  CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY,
  useCliveConfigOptions,
} from '~/components/SystemConfiguration/CliveConfiguration/CliveConfigurationContext';
import TextFieldFormik from '~/components/TextFieldFormik';

import HiddenIndication from '../../HiddenIndication/HiddenIndication';
import ArrayFieldFormik from '../../OptionsCard/NestedFieldFormik/ArrayFieldFormik';
import SwitchOrDelete from '../../OptionsCard/SwitchOrDelete';

const getActions = ({ isActive = false, actions = undefined }) => (
  <div className="flex items-center">
    <HiddenIndication isActive={isActive} />
    {actions}
  </div>
);

const SelectCategory = ({ id }) => {
  const { values } = useFormikContext();
  const categories = get(values, 'configuration.categories', []);

  return (
    <TextFieldFormik id={id} label="Category" select fullWidth disabled={!categories.length} clearable>
      {categories.map((option) => (
        <MenuItem key={option.id} value={option.id}>
          {option.desc}
        </MenuItem>
      ))}
    </TextFieldFormik>
  );
};

SelectCategory.propTypes = {
  id: PropTypes.string,
};

const NESTED_FIELD_CONFIG = [
  { id: 'desc', label: 'Display name', type: 'text' },
  { id: 'id', label: 'Key', type: 'text', disableEditAfterInitialSet: true },
  {
    id: 'category',
    label: 'Category',
    type: 'custom',
    customComponent: SelectCategory,
  },
  {
    id: 'active',
    label: '',
    type: 'custom',
    customComponent: SwitchOrDelete,
  },
];

const NESTED_FIELD_CONFIG_WITH_CLIVE = [
  ...NESTED_FIELD_CONFIG,
  {
    id: CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY,
    label: 'Clive additional instructions',
    type: 'text',
    additionalProps: { fullWidth: true, multiline: true, rows: 3 },
  },
];

const INNER_FIELD_INITIAL_VALUES = {
  id: '',
  desc: '',
  category: undefined,
  active: true,
  [CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY]: '',
};

const IncidentTypeConfigurationCard = ({
  idPrefix,
  fieldId,
  isNewField: isNewFieldProp,
  forceRequired = false,
  disabledCollapse = false,
  actions,
}) => {
  const { values, initialValues, isSubmitting, touched, setFieldValue } = useFormikContext();
  const id = idPrefix ? `${idPrefix}.${fieldId}` : fieldId;
  const field = get(values, id, {});
  const wasKeyTouched = get(touched, `${id}.id`);

  // Newness must be decided by the type's stable id, not its array position: rows are keyed by
  // position, so after a DnD reorder initialValues[index] belongs to a different type and a
  // position check flips new/existing the wrong way, locking a moved new type's Key. The parent
  // owns the id-based identity check (it already uses it for delete) and passes the verdict in;
  // fall back to the position check for callers that don't supply it.
  const isNewField = isNewFieldProp ?? isEmpty(get(initialValues, id, {}));
  const isActive = get(values, `${id}.active`, false);

  const { showCliveConfigOptions } = useCliveConfigOptions();

  const nestedFieldsConfig = useMemo(
    () => (showCliveConfigOptions ? NESTED_FIELD_CONFIG_WITH_CLIVE : NESTED_FIELD_CONFIG),
    [showCliveConfigOptions]
  );

  return (
    <Grid item xs={12}>
      <CollapsibleWrapper
        title={field.desc}
        subtitle={`Key: ${field.id}`}
        backgroundColor={CollapsibleWrapper.BACKGROUND_COLOR.LIST}
        actionCard
        noBorderLine
        noDivider
        actions={getActions({ isActive, actions })}
        disabled={disabledCollapse}
        // As in the sibling FieldConfigurationCard: hug the actions so the org-authored `Key:` gets
        // the whole header instead of wrapping at its midpoint.
        withActionsContainerFullWidth={false}
        defaultState={isNewField}
      >
        {/* Drop the InnerCard's bottom margin: it's a DnD row, and that trailing margin
            otherwise renders as a white strip under the card while dragging (row spacing
            comes from the list gap). */}
        <InnerCard className="!mb-0">
          <Grid container className="flex items-center" spacing={3}>
            <Grid item xs={6}>
              <TextFieldFormik
                id={`${id}.desc`}
                label="Incident Type"
                fullWidth
                // Sync the Key from the name only while the user types (edit-driven), not via a
                // value-watching effect: rows are keyed by position, so a reorder shuffles data
                // between positions and a watcher would read that as an edit and rewrite the key.
                // Condition mirrors the previous guard (!isNewField && !wasKeyTouched disabled it).
                onChange={(e) => {
                  const newDesc = e.target.value;
                  setFieldValue(`${id}.desc`, newDesc);
                  if (isNewField || wasKeyTouched) {
                    setFieldValue(`${id}.id`, snakeCase(newDesc));
                  }
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextFieldFormik id={`${id}.id`} label="Key" fullWidth disabled={!isNewField} />
            </Grid>
            {showCliveConfigOptions ? (
              <Grid item xs={12}>
                <TextFieldFormik
                  id={`${id}.${CLIVE_ADDITIONAL_INSTRUCTIONS_FIELD_KEY}`}
                  label="Clive additional instructions"
                  fullWidth
                  multiline
                  rows={3}
                />
              </Grid>
            ) : null}
            <Grid item xs={2}>
              <SwitchFormik id={`${id}.active`} label="Show" hideErrorGap disabled={forceRequired} />
            </Grid>
            <Grid item xs={12}>
              <CardDialog noCardTitle contentStyle={{ padding: '16px' }}>
                <ArrayFieldFormik
                  fieldId={`${id}.sub_types`}
                  label="Sub-Types"
                  innerObjectConfig={nestedFieldsConfig}
                  disabled={isSubmitting}
                  initialNewItemValues={INNER_FIELD_INITIAL_VALUES}
                  addBtnLabel="ADD SUBTYPE"
                  withDndReorder
                />
              </CardDialog>
            </Grid>
          </Grid>
        </InnerCard>
      </CollapsibleWrapper>
    </Grid>
  );
};

IncidentTypeConfigurationCard.propTypes = {
  idPrefix: PropTypes.string,
  fieldId: PropTypes.string.isRequired,
  isNewField: PropTypes.bool,
  savedValues: PropTypes.object,
  forceRequired: PropTypes.bool,
  isConfiguredField: PropTypes.bool,
  disabledCollapse: PropTypes.bool,
  actions: PropTypes.node,
};

export default IncidentTypeConfigurationCard;
